import React from "react";
import { Table, TableHead } from "@material-ui/core";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import { getProgenyDisplayCount } from "lib/saleLot";
import { useSelector } from "react-redux";
import groupBy from "lodash/groupBy";
import { selectSaleProgressSaleLots } from "selectors";
import { formatDecimal, formatNumber } from "lib";
import { sortBy } from "lodash";

interface AttributeTableRowProps {
  attributeName: string;
  count: number;
  headCount: number;
  progenyCount: number;
  soldHeadCount: number;
  soldProgenyCount: number;
  totalPriceCents: number;
  averagePriceCents: number;
  maxPriceCents: number;
}
export function AttributeTableRow(
  props: AttributeTableRowProps,
): React.JSX.Element {
  const {
    attributeName,
    count,
    headCount,
    progenyCount,
    soldHeadCount,
    soldProgenyCount,
    totalPriceCents,
    averagePriceCents,
    maxPriceCents,
  } = props;
  return (
    <TableRow>
      <TableCell>{attributeName}</TableCell>
      <TableCell align="right">{formatNumber(count)}</TableCell>
      <TableCell align="right">
        {`${formatNumber(headCount)} ${getProgenyDisplayCount(progenyCount)}`}
      </TableCell>
      <TableCell align="right">{`${formatNumber(soldHeadCount)} ${getProgenyDisplayCount(soldProgenyCount)}`}</TableCell>
      <TableCell align="right">
        {`${formatDecimal(totalPriceCents * 0.01)}`}
      </TableCell>
      <TableCell align="right">
        {`${formatDecimal(averagePriceCents * 0.01)}`}
      </TableCell>
      <TableCell align="right">{formatDecimal(maxPriceCents * 0.01)}</TableCell>
    </TableRow>
  );
}
export function summariseSaleLots(saleLots: SaleLot[]) {
  const counts = saleLots.reduce(
    (acc, saleLot) => {
      acc.headCount += saleLot.quantity || 0;
      acc.soldHeadCount += saleLot.is_sold ? saleLot.quantity : 0;
      acc.progenyCount += saleLot.quantityProgeny || 0;
      acc.soldProgenyCount += saleLot.is_sold ? saleLot.quantityProgeny : 0;
      acc.totalPriceCents += saleLot.total_price_cents || 0;
      return acc;
    },
    {
      maxPriceCents: 0,
      headCount: 0,
      soldHeadCount: 0,
      progenyCount: 0,
      soldProgenyCount: 0,
      totalPriceCents: 0,
    },
  );

  const averagePriceCents =
    counts.soldHeadCount > 0
      ? counts.totalPriceCents / counts.soldHeadCount
      : 0;

  const maxPriceCents = Math.max(
    ...saleLots.map(
      saleLot => (saleLot.total_price_cents || 0) / (saleLot.quantity || 1),
    ),
  );
  return {
    ...counts,
    averagePriceCents,
    maxPriceCents,
  };
}

interface AttributeGroup {
  saleLots: SaleLot[];
  groupName: string;
  groupId: string;
}

interface TableProps {
  attributeGroups: AttributeGroup[];
  name: string;
}
export function AttributeTable(props: TableProps): React.JSX.Element {
  const { attributeGroups, name } = props;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{name}</TableCell>
          <TableCell align="right">Lots</TableCell>
          <TableCell align="right">Hd</TableCell>
          <TableCell align="right">Sold</TableCell>
          <TableCell align="right">Total $</TableCell>
          <TableCell align="right">Average $/Hd</TableCell>
          <TableCell align="right">Top $/Hd</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {attributeGroups.map(({ groupId, saleLots, groupName }) => {
          const {
            averagePriceCents,
            headCount,
            maxPriceCents,
            soldHeadCount,
            progenyCount,
            soldProgenyCount,
            totalPriceCents,
          } = summariseSaleLots(saleLots);

          return (
            <AttributeTableRow
              key={groupId}
              count={saleLots.length}
              attributeName={groupName}
              headCount={headCount}
              progenyCount={progenyCount}
              soldHeadCount={soldHeadCount}
              soldProgenyCount={soldProgenyCount}
              totalPriceCents={totalPriceCents}
              averagePriceCents={averagePriceCents}
              maxPriceCents={maxPriceCents}
            />
          );
        })}
      </TableBody>
    </Table>
  );
}

export function groupSaleLots(saleLots: SaleLot[], groupee, namee) {
  const groupedBy = groupBy(saleLots, groupee);

  const groups = Object.entries(groupedBy).map(
    ([groupId, groupedSaleLots]) => ({
      groupId,
      groupName: namee(groupId),
      saleLots: groupedSaleLots,
    }),
  );

  return sortBy(groups, group => (group.groupId ? group.groupName : null));
}

export interface SaleProgressSpeciesAttributeTableProps<ReduxState> {
  saleLotAttributeField: string;
  name: string;
  attributeLookupSelector: (
    state: ReduxState,
  ) => LookupSelectorResult<{ name: string }>;
}

export function SaleProgressTotalsAttributeTable<ReduxState>(
  props: SaleProgressSpeciesAttributeTableProps<ReduxState>,
): React.JSX.Element {
  const { attributeLookupSelector, name, saleLotAttributeField } = props;
  const saleLots = useSelector(selectSaleProgressSaleLots) || [];
  const attributeByIdLookup = useSelector(attributeLookupSelector);

  const attributeGroups = groupSaleLots(
    saleLots,
    sl => sl[saleLotAttributeField],
    id => (id !== "null" ? attributeByIdLookup[id]?.name : "(No Data)"),
  );

  return <AttributeTable attributeGroups={attributeGroups} name={name} />;
}
